import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { colors } from '@/constants/colors';
import { uploadImage } from '@/api/store';

/**
 * The business logo, as a circular avatar you click.
 *
 * Ray, 2026-08-31: *"make the logo upload UI like an avatar, a circle
 * clickable when upload/reupload no need remove button."* The generic uploader
 * put "Uploaded", "View original", "Replace" and a red "Remove" in a box beside
 * a thumbnail — four controls, overlapping, for a field whose only real action
 * is "use a different picture".
 *
 * So: one circle. Empty, it shows the business initials and invites a click.
 * Filled, it shows the logo and the same click replaces it. No Remove — a logo
 * is either the old one or a new one, and an operator who wants none simply
 * never sets it. Removing it is not a thing anybody has ever needed to do
 * urgently enough to earn a red button next to their brand.
 */
interface Props {
   value: string;
   onChange: (url: string) => void;
   /** Seeds the placeholder initials before there is a logo. */
   name?: string;
}

const IspLogoAvatar: React.FC<Props> = ({ value, onChange, name }) => {
   const input = useRef<HTMLInputElement>(null);
   const [busy, setBusy] = useState(false);

   const initials = (name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase() ?? '')
      .join('');

   const pick = async (file: File | undefined) => {
      if (!file) return;
      // Checked here rather than only server-side: an operator who picks a 12MB
      // photo should be told before the upload, not after it.
      if (!file.type.startsWith('image/')) {
         toast.error('Pick an image file.');
         return;
      }
      if (file.size > 5 * 1024 * 1024) {
         toast.error('That image is over 5MB. Pick a smaller one.');
         return;
      }
      setBusy(true);
      try {
         // The same uploader every other image in the panel goes through, so a
         // logo lands where invoices and the payment page already look.
         const { imageUrl } = await uploadImage(file);
         if (!imageUrl) throw new Error('no url');
         onChange(imageUrl);
      } catch {
         toast.error('Could not upload that image. Try again.');
      } finally {
         setBusy(false);
         // Cleared so picking the SAME file again still fires a change.
         if (input.current) input.current.value = '';
      }
   };

   return (
      <Wrap>
         <Circle
            type="button"
            $has={!!value}
            disabled={busy}
            onClick={() => input.current?.click()}
            title={value ? 'Click to replace your logo' : 'Click to add your logo'}
         >
            {value ? <Img src={value} alt="" /> : <Initials>{initials || '＋'}</Initials>}
            <Veil className="veil">{busy ? '…' : value ? 'Replace' : 'Upload'}</Veil>
         </Circle>
         <HiddenInput
            ref={input}
            type="file"
            accept="image/*"
            onChange={(e) => pick(e.target.files?.[0])}
         />
      </Wrap>
   );
};

export default IspLogoAvatar;

const Wrap = styled.div`
   display: flex;
   align-items: center;
   gap: 12px;
`;

const HiddenInput = styled.input`
   display: none;
`;

const Circle = styled.button<{ $has: boolean }>`
   position: relative;
   width: 84px;
   height: 84px;
   flex: 0 0 auto;
   border-radius: 50%;
   overflow: hidden;
   cursor: pointer;
   padding: 0;
   background: ${({ $has }) => ($has ? colors.WHITE : colors.GRAY14)};
   border: 1px ${({ $has }) => ($has ? 'solid' : 'dashed')} ${colors.GRAY12};
   display: grid;
   place-items: center;
   &:hover .veil,
   &:focus-visible .veil {
      opacity: 1;
   }
   &:disabled {
      cursor: default;
   }
`;

const Img = styled.img`
   width: 100%;
   height: 100%;
   object-fit: cover;
`;

const Initials = styled.span`
   font-size: 24px;
   font-weight: 700;
   color: ${colors.GRAY5};
`;

/** The only control. Hidden until hover, so the logo is not permanently labelled. */
const Veil = styled.span`
   position: absolute;
   inset: 0;
   display: grid;
   place-items: center;
   font-size: 11px;
   font-weight: 700;
   letter-spacing: 0.04em;
   text-transform: uppercase;
   color: ${colors.WHITE};
   background: rgba(0, 0, 0, 0.45);
   opacity: 0;
   transition: opacity 0.15s;
`;
